import './ProductStickyFooterA.scss';

import classnames from 'classnames';
import React from 'react';

import { AddToCartFormButton } from './AddToCartFormButton';
import { Button } from './Button';
import { appConfig } from './config';
import { Container } from './Container';
import { useFloatingActionButtonContext } from './FloatingActionButtonContext';
import { Div, Span } from './HTMLElement';
import { t } from './i18n/i18n';
import { ImageSwitcher } from './ImageSwitcher';
import { PriceBlock } from './PriceBlock';
import { ProductStickyFooterProps } from './ProductStickyFooter';
import { useBreakpoint } from './useBreakpoint';
import { untilBreakpoint } from './util';

export const ProductStickyFooterE = <ButtonVariant extends string, IconName extends string, IconSize extends number>({
  fieldsHelper,
  formik,
  isVisible,
  priceHelper,
  product,
  scrollToElement,
  selectedProductAttributes,
  selectedProductImage,
  selectedSku,
  submitDesktopButton,
  submitMobileButton,
}: ProductStickyFooterProps<ButtonVariant, IconName, IconSize>) => {
  const handleSelectSizeButtonClick = React.useCallback(() => {
    scrollToElement && scrollToElement();
    formik.submitForm();
  }, [formik, scrollToElement]);

  const stickyFooterRef = React.useRef<HTMLDivElement>(null);
  const { addOffset, removeOffset } = useFloatingActionButtonContext();

  React.useEffect(() => {
    if (isVisible && stickyFooterRef.current) {
      addOffset('productStickyFooter', stickyFooterRef.current.offsetHeight);
    } else {
      removeOffset('productStickyFooter');
    }
    return () => {
      removeOffset('productStickyFooter');
    };
  }, [addOffset, isVisible, removeOffset]);
  const currentBreakpoint = useBreakpoint();

  return (
    <Div innerRef={stickyFooterRef} uiClassName={{ bs5: 'shadow-sm' }}>
      <Div
        aria-label={t('accessibility.product_stickyBar')}
        className={classnames('product-sticky-footer', {
          show: isVisible,
        })}
        role="region"
        style={{ position: 'fixed', width: '100%', zIndex: 1000 }}
        uiClassName={{ bs5: 'shadow-lg' }}
      >
        <Div
          className="product-sticky-footer-mobile"
          display={{ lg: 'none', xs: 'flex' }}
          flexDirection={'column'}
          gap={2}
          padding={2}
        >
          <Div
            alignItems={'center'}
            display={'flex'}
            justifyContent={'spaceBetween'}
            textAlign={'center'}
            uiClassName={{ bs5: 'extra-small' }}
          >
            <Div alignItems={'center'} display={'flex'} gap={3} justifyContent={'start'}>
              <Div className="product-name" style={{ maxWidth: 150 }}>
                <b>{product.name}</b>
              </Div>
              {selectedProductAttributes?.map((attribute) => (
                <Div key={attribute.typeCode} paddingStart={3} uiClassName={{ bs5: 'border-start' }}>
                  {attribute.value}
                </Div>
              ))}
            </Div>
            <Div uiClassName={{ bs5: 'flex-grow-0 small' }}>
              <PriceBlock gap={2} priceHelper={priceHelper} />
            </Div>
          </Div>
          <Div display={'grid'} paddingTop={2} uiClassName={{ bs5: 'border-top' }}>
            {selectedSku ? (
              <AddToCartFormButton
                availabilityEmailNotificationButton={{
                  label: untilBreakpoint('xs', currentBreakpoint)
                    ? t('form.addToCartForm.buttons.availabilityEmailNotification.addShort.label')
                    : t('form.addToCartForm.buttons.availabilityEmailNotification.addLong.label'),
                }}
                fieldsHelper={fieldsHelper}
                product={product}
                selectedSku={selectedSku}
                submitButton={{ ...submitMobileButton, onClick: formik.submitForm }}
              />
            ) : (
              <Button
                label={t('product.selectSize')}
                onClick={handleSelectSizeButtonClick}
                variant={submitMobileButton?.variant}
              />
            )}
          </Div>
        </Div>
        <Div display={{ lg: 'block', xs: 'none' }}>
          <Container>
            <Div alignContent={'center'} display={'flex'} justifyContent={'spaceBetween'} paddingY={2}>
              <Div alignItems={'center'} display={'flex'} gap={3} justifyContent={'start'}>
                <Div alignItems={'center'} display={'flex'}>
                  <Div marginEnd={3} uiClassName={{ bs5: 'image-wrapper rounded overflow-hidden border' }}>
                    <ImageSwitcher
                      alt={product.name}
                      aspectRatio={appConfig.productImageAspectRatio}
                      fluid
                      src={selectedSku?.image?.src ?? selectedProductImage?.src}
                    />
                  </Div>
                  <Div className="product-name h6" fontWeight={500} margin={{ xs: 0 }} style={{ maxWidth: '225px' }}>
                    <b>{product.name}</b>
                  </Div>
                </Div>
                <Div display={'flex'} gap={4} style={{ marginBlock: 'auto' }}>
                  {selectedProductAttributes &&
                    selectedProductAttributes.map((attribute, index) => (
                      <Div key={attribute.typeCode} paddingStart={4} uiClassName={{ bs5: 'border-start' }}>
                        <Span uiClassName={{ bs5: 'me-2' }}>{attribute.title}:</Span>
                        <b>{attribute.value}</b>
                      </Div>
                    ))}
                </Div>
              </Div>
              <Div alignItems={'center'} display={'flex'} gap={3} justifyContent={'start'}>
                <Div style={{ marginBlock: 'auto' }}>
                  <PriceBlock gap={2} priceHelper={priceHelper} />
                </Div>
                <Div style={{ marginBlock: 'auto' }} uiClassName={{ bs5: 'text-uppercase' }}>
                  {selectedSku ? (
                    <AddToCartFormButton
                      fieldsHelper={fieldsHelper}
                      product={product}
                      selectedSku={selectedSku}
                      submitButton={{ ...submitDesktopButton, onClick: formik.submitForm }}
                    />
                  ) : (
                    <Button
                      label={t('product.selectSize')}
                      onClick={handleSelectSizeButtonClick}
                      variant={submitDesktopButton?.variant}
                    />
                  )}
                </Div>
              </Div>
            </Div>
          </Container>
        </Div>
      </Div>
    </Div>
  );
};
